/**
 * OnboardingShell — корневой overlay онбординга Luna Protokol.
 *
 * Рендерится как fixed-overlay поверх основного AppShell,
 * но стартует не сразу:
 *
 *   1. При монтировании → fetch /api/luna/onboarding/status
 *   2. Если shouldShow === false — ничего не рендерит никогда
 *      (юзер уже прошёл/скипнул ранее, overlay не появится).
 *   3. Если shouldShow === true — ждёт ONBOARDING_INITIAL_DELAY_MS
 *      (2 сек, даём юзеру осмотреть главный экран + догрузиться
 *      данные в фоне), затем fade-in overlay 700мс.
 *   4. Оверлей непрозрачный: закрывает ВЕСЬ main UI, чтобы
 *      онбординг был фокусом внимания.
 *   5. При complete / skip → вызывает onboarding API → закрывается
 *      fade-out 450мс → unmount'ится.
 *
 * Внутри overlay:
 *   - Gradient mesh фон (conic 4 точки, 40s rotate)
 *   - Noise overlay 4% soft-light
 *   - Текущий шаг из state (AnimatePresence mode='wait')
 *   - SkipButton в правом верхнем углу (кроме последнего шага)
 *   - Bottom nav для "Далее" (кроме sandbox — там авто)
 *
 * Управление шагами:
 *   step 1 — WelcomeStep (hero moon + Luna PROTOKOL)
 *   step 2 — StrategyStep ("Зачем вы здесь?")
 *   step 3 — CalculatorStep (слайдер + прогноз)
 *   step 4 — SandboxStep (30 сек демо)
 *   step 5 — FinaleStep (+$1 welcome bonus + CTA)
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { lunaApi } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { SkipButton } from './SkipButton';
import { OnboardingBackdrop } from './OnboardingBackdrop';
import { WelcomeStep } from './steps/WelcomeStep';
import { StrategyStep } from './steps/StrategyStep';
import { CalculatorStep } from './steps/CalculatorStep';
import { SandboxStep } from './steps/SandboxStep';
import { FinaleStep } from './steps/FinaleStep';
import {
  EASE_SCREEN,
  EASE_EXIT,
  ONBOARDING_INITIAL_DELAY_MS,
} from './springs';

type Strategy = 'passive' | 'growth' | 'network';

interface OnboardingStatus {
  currentStep: number;
  startedAt: string | null;
  completedAt: string | null;
  strategyChoice: Strategy | null;
  skipped: boolean;
  bonusReceived: boolean;
  checklist: Record<string, boolean>;
  shouldShow: boolean;
}

type Phase = 'idle' | 'loading' | 'waiting' | 'active' | 'closing' | 'done';

export function OnboardingShell() {
  const { refresh: refreshAuth, user } = useAuth();
  const [phase, setPhase] = useState<Phase>('loading');
  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [localStep, setLocalStep] = useState(1);
  const [strategy, setStrategy] = useState<Strategy | null>(null);
  const [demoAmount, setDemoAmount] = useState(5000); // из калькулятора, дефолт $5k
  const [withNetwork, setWithNetwork] = useState(false); // тоггл «развивать сеть»
  const bonusReceivedOnFinalRef = useRef<{ credited: boolean; newBalance: string } | null>(null);

  // ── initial status fetch ────────────────────────────────────────
  // Ждём появления user из AuthContext — только тогда запрашиваем
  // /onboarding/status. Без этого у новых юзеров статус check
  // срабатывал ДО завершения /auth/telegram → 401 → phase='done'
  // и онбординг появлялся только при втором заходе.
  useEffect(() => {
    if (!user?.id) return; // JWT ещё не готов — ждём
    if (phase === 'active' || phase === 'closing' || phase === 'done') return;

    let cancelled = false;
    let delayTimer: number | undefined;
    const retryTimers: number[] = [];

    // Retry-обёртка: на слабых сетях (3G, Турция и т.п.) fetch может
    // упасть или зависнуть. Раньше catch оставлял phase='loading' —
    // юзер залипал на пустом экране навсегда. Теперь:
    //   • 3 попытки с exponential backoff 1s / 3s / 7s
    //   • каждый запрос с AbortController timeout 8s
    //   • если все попытки провалились — phase='done' (онбординг
    //     пропускается, юзер попадает в Dashboard, не виснет).
    // Таким образом БЕЗ онбординга он хотя бы войдёт в приложение.
    const fetchStatusWithRetry = async (attempt = 1): Promise<void> => {
      if (cancelled) return;
      const ctrl = new AbortController();
      const timeoutId = window.setTimeout(() => ctrl.abort(), 8000);
      try {
        const res = await lunaApi.onboarding.status({ signal: ctrl.signal });
        window.clearTimeout(timeoutId);
        const data = res.data as { success: boolean; status: OnboardingStatus };
        if (cancelled) return;

        if (!data.success || !data.status.shouldShow) {
          setPhase('done');
          return;
        }

        try {
          window.localStorage.removeItem('luna_tour_seen');
        } catch {
          // silent
        }

        setStatus(data.status);
        setStrategy(data.status.strategyChoice);
        setLocalStep(Math.max(1, data.status.currentStep));

        if (ONBOARDING_INITIAL_DELAY_MS > 0) {
          setPhase('waiting');
          delayTimer = window.setTimeout(() => {
            if (cancelled) return;
            setPhase('active');
            if (!data.status.startedAt) {
              void lunaApi.onboarding.start().catch(() => {
                // silent — не блокер для UI
              });
            }
          }, ONBOARDING_INITIAL_DELAY_MS);
        } else {
          setPhase('active');
          if (!data.status.startedAt) {
            void lunaApi.onboarding.start().catch(() => {});
          }
        }
      } catch (err) {
        window.clearTimeout(timeoutId);
        if (cancelled) return;
        if (attempt < 3) {
          const backoff = attempt === 1 ? 1000 : 3000;
          const t = window.setTimeout(
            () => fetchStatusWithRetry(attempt + 1),
            backoff,
          );
          retryTimers.push(t);
        } else {
          // Все попытки провалились — пропускаем онбординг вместо
          // бесконечного висящего пустого экрана. Юзер увидит Dashboard.
          // eslint-disable-next-line no-console
          console.warn('onboarding status fetch failed after 3 retries', err);
          setPhase('done');
        }
      }
    };

    void fetchStatusWithRetry();

    return () => {
      cancelled = true;
      if (delayTimer) window.clearTimeout(delayTimer);
      retryTimers.forEach((t) => window.clearTimeout(t));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // ── step transitions ─────────────────────────────────────────────
  const goToStep = useCallback((next: number) => {
    setLocalStep(next);
    void lunaApi.onboarding.step(next).catch(() => {
      // silent — шаги идемпотентны
    });
  }, []);

  const handleStrategyChosen = useCallback(
    (s: Strategy) => {
      setStrategy(s);
      void lunaApi.onboarding.strategy(s).catch(() => {});
      goToStep(3);
    },
    [goToStep],
  );

  const handleSkip = useCallback(async () => {
    setPhase('closing');
    try {
      await lunaApi.onboarding.skip();
    } catch {
      // silent
    }
    window.setTimeout(() => setPhase('done'), 500);
  }, []);

  const handleComplete = useCallback(async () => {
    try {
      const res = await lunaApi.onboarding.complete();
      const data = res.data as {
        success: boolean;
        bonusCredited: boolean;
        newBalance: string;
      };
      bonusReceivedOnFinalRef.current = {
        credited: data.bonusCredited,
        newBalance: data.newBalance,
      };
      // Обновляем баланс в AuthContext чтобы balance pill в шапке показал $1
      void refreshAuth();
    } catch {
      // silent — finale-экран всё равно показывает сообщение
    }
  }, [refreshAuth]);

  const handleFinish = useCallback(() => {
    setPhase('closing');
    // Оповещаем CoachMarkTour что можно стартовать (тост о $1
    // диспатчится CoachMarkTour-ом в самом конце — после финального окна)
    window.dispatchEvent(new CustomEvent('luna:onboarding-done'));
    window.setTimeout(() => setPhase('done'), 500);
  }, []);

  // Не рендерим ничего если не нужно
  if (phase === 'done' || phase === 'loading' || phase === 'waiting' || !status) {
    return null;
  }

  // Skip доступен только на шагах 1-3 (на 4+ уже слишком близко к бонусу)
  const skipVisible = localStep <= 3 && phase === 'active';

  return (
    <AnimatePresence>
      {phase !== 'closing' && (
        <motion.div
          key="onboarding-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={EASE_SCREEN}
          className="fixed inset-0 z-[9000] overflow-hidden"
          style={{
            touchAction: 'none',
          }}
        >
          {/* Gradient mesh + specular + noise фон */}
          <OnboardingBackdrop />

          {/* Skip button в углу */}
          <SkipButton onSkip={handleSkip} visible={skipVisible} />

          {/* Экраны шагов */}
          <div className="relative z-10 w-full h-full">
            <AnimatePresence mode="wait" initial={false}>
              {localStep === 1 && (
                <motion.div
                  key="step-welcome"
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -18, transition: EASE_EXIT }}
                  transition={EASE_SCREEN}
                  className="absolute inset-0 flex items-center justify-center"
                >
                  <WelcomeStep onNext={() => goToStep(2)} />
                </motion.div>
              )}

              {localStep === 2 && (
                <motion.div
                  key="step-strategy"
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -18, transition: EASE_EXIT }}
                  transition={EASE_SCREEN}
                  className="absolute inset-0 flex items-center justify-center"
                >
                  <StrategyStep
                    current={strategy}
                    onChoose={handleStrategyChosen}
                  />
                </motion.div>
              )}

              {localStep === 3 && (
                <motion.div
                  key="step-calculator"
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -18, transition: EASE_EXIT }}
                  transition={EASE_SCREEN}
                  className="absolute inset-0 flex items-center justify-center"
                >
                  <CalculatorStep
                    strategy={strategy}
                    onAmountChange={setDemoAmount}
                    onNetworkChange={setWithNetwork}
                    withNetwork={withNetwork}
                    onNext={() => goToStep(4)}
                  />
                </motion.div>
              )}

              {localStep === 4 && (
                <motion.div
                  key="step-sandbox"
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -18, transition: EASE_EXIT }}
                  transition={EASE_SCREEN}
                  className="absolute inset-0 flex items-center justify-center"
                >
                  <SandboxStep
                    demoAmount={demoAmount}
                    withNetwork={withNetwork}
                    onFinish={() => {
                      void handleComplete();
                      goToStep(5);
                    }}
                  />
                </motion.div>
              )}

              {localStep === 5 && (
                <motion.div
                  key="step-finale"
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -18, transition: EASE_EXIT }}
                  transition={EASE_SCREEN}
                  className="absolute inset-0 flex items-center justify-center"
                >
                  <FinaleStep
                    bonusInfo={bonusReceivedOnFinalRef.current}
                    onFinish={handleFinish}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
